/**
 * SPR — Spelling Regels rulebook.
 *
 * Sourced from DEFAULT_POLICY (SpellingRegel entries) +
 * docs/audits/DIDACTISCH_AUDIT_SPR_2026.md + MODULE_AUTHORING_GUIDE §6.
 *
 * v2 (mei 2026, sessie 2) — uitgebreid n.a.v.
 * docs/audits/VISUALS_QUESTIONTYPES_RULEBOOK_AUDIT_2026.md (P1-1):
 *   - customTypeRoles toegevoegd voor categorize / error_spotting /
 *     drag_drop_fill / text_highlight, zodat de pedagogy-stage DOK-alignment
 *     voor deze types weet binnen SPR.
 *   - discouragedTypes: true_false_series (recognition-only, geeft geen
 *     spellingsregel-toepassing).
 *   - familyVisualHints uitgebreid met meer nuance per klank-/weet-/leen-/
 *     sam-familie, en met DOK-niveau-onderbouwing in de notes.
 *   - preferredAidTypes uitgebreid met kofschipMnemonic (hoewel die primair
 *     in SWW leeft, hier expliciet niet preferred — vermijdt cross-module
 *     drift).
 */

import type { ModuleRulebook } from './types'

export const SPR_RULEBOOK: ModuleRulebook = {
  moduleId: 'SpellingRegel',
  shortCode: 'SPR',
  displayName: 'Spelling Regels',

  didactics: {
    principles: [
      'open_gesloten_lettergreep',
      'klank_versus_schrijf',
      'leenwoorden',
      'samenstellingen',
      'regel_toepassen_niet_herkennen',
    ],
    schoolbookReferences: ['Staal Spelling', 'Taal Actief'],
    misconceptionDomain: 'spr',
    coreSummary:
      'Spellingregels: open/gesloten lettergrepen, verdubbelen/verkorten, trema, leenwoorden, samenstellingen. Toepassing op nieuw woord > herkennen van bekend woord.',
  },

  notation: {},

  content: {
    distractorMisconceptionPattern:
      'open lettergreep|gesloten lettergreep|trema|koppelteken|tussen-n|samengesteld|leenwoord|verdubbelen|verkorten',
  },

  explanation: {
    requireRuleReference: true,
    checkStrategyMinTier: 9,
    preferredStrategyKeywords: [
      'open lettergreep',
      'gesloten lettergreep',
      'verdubbelen',
      'verkorten',
      'trema',
      'meervoud',
      'leenwoord',
    ],
  },

  visuals: {
    preferredAidTypes: ['syllableBlocks', 'rule'],
    discouragedAidTypes: [
      // kofschipMnemonic is een SWW-werkwoord-visual (d/t in verleden tijd);
      // gebruik hier verstoort de scheiding SPR (woordbeeld) vs SWW
      // (werkwoordsvervoeging). Past niet bij SPR-WEET-D-T (zelfstandig
      // naamwoord-spelling).
      'kofschipMnemonic',
    ],
    familyVisualHints: [
      // — Klankregels: lettergreep-analyse is de kern —
      {
        familyIdPattern: '^SPR-KLANK-(AA-EE-OO|AU-OU)',
        preferredSupport: 'syllableBlocks',
        note: 'Klinker-onderscheid: lettergrepen splitsen toont waarom open lettergreep lange klank krijgt.',
      },
      {
        familyIdPattern: '^SPR-KLANK-(VERDUBBEL|VERKORTEN)',
        preferredSupport: 'syllableBlocks',
        note: 'Verdubbel/verkort: visueel zien dat gesloten lettergreep medeklinker-verdubbeling triggert (man → mannen).',
      },
      {
        familyIdPattern: '^SPR-KLANK-(JAGER|BAKKER)',
        preferredSupport: 'syllableBlocks',
        note: 'Open/gesloten lettergreep-trick: ja-ger (open, lange a) vs bak-ker (gesloten, korte a).',
      },
      {
        familyIdPattern: '^SPR-KLANK-',
        preferredSupport: 'syllableBlocks',
        autoExemptEmbedded: true,
        note: 'Default voor klankregels: lettergrepen visueel splitsen helpt klankregel-besluit.',
      },

      // — Weet-regels: regel-kaart met expliciete uitzonderingen —
      {
        familyIdPattern: '^SPR-WEET-(D-T|DT)',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'D/t-keuze in zelfstandig naamwoorden: regel-kaart met meervoud-check (hond → honden → hond met d).',
      },
      {
        familyIdPattern: '^SPR-WEET-EI-IJ',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Ei/ij: pure weet-regel, geen klankonderscheid → regel-kaart met meest voorkomende families en uitzonderingen.',
      },
      {
        familyIdPattern: '^SPR-WEET-',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Default voor weet-regels: regel-kaart als geheugensteun.',
      },

      // — Trema: regel-kaart, want voornamelijk weet-regel —
      {
        familyIdPattern: '^SPR-TREMA',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Trema voorkomt verkeerde klinker-samenvoeging (zee-en → zeeën); regel-kaart met meervoudspatroon.',
      },

      // — Leenwoorden: per-bron-regel met taalspecifieke patronen —
      {
        familyIdPattern: '^SPR-LEEN-ENGELS',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Engelse leenwoorden: spelling vaak Engels behouden (manager, computer); regel-kaart per categorie (vakwoorden, internet).',
      },
      {
        familyIdPattern: '^SPR-LEEN-FRANS',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Franse leenwoorden: ai/au/eau/é-spelling; regel-kaart met fonologische voorbeelden (cadeau, bureau, café).',
      },
      {
        familyIdPattern: '^SPR-LEEN-GRIEKS',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Griekse leenwoorden: ch/th/ph en y-spelling (chronisch, theater, fysica); regel-kaart per fonologische groep.',
      },
      {
        familyIdPattern: '^SPR-LEEN-LATIJN',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Latijnse leenwoorden: -tie/-isch/-um-eindes; regel-kaart per achtervoegsel.',
      },
      {
        familyIdPattern: '^SPR-LEEN-C-S-K',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'C/s/k-onderscheid in leenwoorden: regel-kaart met klank-naar-letter-mapping (cel/sel, café/kafé).',
      },

      // — Samenstellingen (tussen-n / tussen-s) —
      {
        familyIdPattern: '^SPR-SAM-TUSSEN-N',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Tussen-n: meervoud-uitsluitings-regel (boekenkast: meervoud "boeken" → tussen-n). Regel-kaart met beheers-toets.',
      },
      {
        familyIdPattern: '^SPR-SAM-TUSSEN-S',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Tussen-s: klank-test (stadhuis → tussen-s hoor je); regel-kaart met klanktoets.',
      },

      // — Leestekens, hoofdletters, accenten —
      {
        familyIdPattern: '^SPR-LEESTEKEN-',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Leestekens (punt/komma/vraag/uitroep): regel-kaart met zinsfunctie per teken.',
      },
      {
        familyIdPattern: '^SPR-HOOFDLETTER',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Hoofdletters: regel-kaart per categorie (zinsbegin, eigennaam, namen van talen/dagen).',
      },
      {
        familyIdPattern: '^SPR-ACCENT',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Accenttekens (é, ë, è): regel-kaart met beklemtoning- en herkomst-regel.',
      },
    ],
  },

  questionTypes: {
    preferredTypes: [
      'multiple_choice',
      'fill_in',
      'categorize',
      'error_spotting',
      'drag_drop_fill',
      'text_highlight',
    ],
    discouragedTypes: [
      {
        type: 'true_false_series',
        reason:
          'Recognition-only — kind hoeft de regel niet toe te passen, alleen "goed/fout" oordelen. Voor SPR is regel-toepassing op nieuw woord essentieel (DOK 2-3), niet herkenning (DOK 1).',
      },
    ],
    customTypeRoles: [
      {
        type: 'categorize',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 16 },
        reason:
          'Woorden indelen in open/gesloten-lettergreep of klank-categorie dwingt regel-toepassing — sterk didactisch alternatief voor MC.',
      },
      {
        type: 'error_spotting',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 7, max: 16 },
        reason:
          'Spelfout vinden in zin vereist meta-cognitie over de regel — kerntaak voor zelfstandig schrijfwerk in groep 7-8.',
      },
      {
        type: 'drag_drop_fill',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 4, max: 16 },
        reason:
          'Letter/lettergreep-fragmenten in juiste positie slepen activeert woordbeeld actiever dan typen.',
      },
      {
        type: 'text_highlight',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 6, max: 16 },
        reason:
          'Specifieke woordvorm (b.v. samenstelling met tussen-n) markeren in zinscontext koppelt regel aan echte tekst.',
      },
      {
        type: 'fill_in',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 3, max: 16 },
        reason:
          'Ontbrekende letter/lettergreep invullen is directe regel-toepassing zonder afleidingseffect van distractors.',
      },
    ],
    familyTypeHints: [
      {
        familyIdPattern: '^SPR-KLANK-',
        preferredTypes: [
          'error_spotting',
          'drag_drop_fill',
          'categorize',
          'fill_in',
        ],
        discouragedTypes: ['true_false_series'],
        note: 'Klankregels (open/gesloten lettergreep, verdubbelen, verkorten) vereisen regel-toepassing op nieuw woord, geen herkenning.',
      },
      {
        familyIdPattern: '^SPR-WEET-',
        preferredTypes: ['fill_in', 'error_spotting', 'multiple_choice'],
        discouragedTypes: ['true_false_series'],
        note: 'Weet-regels (d/t, ei/ij): toepassing vereist juiste letter invullen, niet beoordelen.',
      },
      {
        familyIdPattern: '^SPR-(LEEN|SAM)-',
        preferredTypes: [
          'categorize',
          'matching',
          'fill_in',
          'multiple_choice',
        ],
        discouragedTypes: ['true_false_series'],
        note: 'Leenwoorden en samenstellingen: categoriseren naar bron-taal of meervoudspatroon — actieve classificatie.',
      },
      {
        familyIdPattern: '^SPR-(TREMA|HOOFDLETTER|LEESTEKEN|ACCENT)',
        preferredTypes: ['error_spotting', 'fill_in', 'text_highlight'],
        note: 'Trema/hoofdletter/leestekens/accent: in zinscontext herkennen + corrigeren.',
      },
    ],
  },

  distractorTraps: [
    {
      id: 'syllable-misanalysis',
      label: 'Lettergreep verkeerd geanalyseerd',
      description:
        'Open lettergreep als gesloten lezen (of andersom) → verkeerde verdubbel/verkort-keuze.',
    },
    {
      id: 'homophone',
      label: 'Homofoon',
      description:
        'Woord met dezelfde klank maar andere spelling kiezen (b.v. wij/wei, ei/ij).',
    },
    {
      id: 'wrong-language-source',
      label: 'Verkeerde bron-taal toegepast',
      description:
        'Engelse spelling toepassen op Frans leenwoord (b.v. "cafe" i.p.v. "café").',
    },
    {
      id: 'tussen-letter-confused',
      label: 'Tussen-n / tussen-s verwisseld',
      description:
        'Tussen-s gebruiken waar tussen-n hoort (of andersom) — meervoudsregel niet toegepast.',
    },
  ],

  keyRules: [
    'Lettergreep-analyse altijd tonen in uitleg bij klankregels',
    'Trema-regel expliciet vermelden met meervoudspatroon',
    'Open/gesloten lettergreep-onderscheid eerst benoemen voor verdubbel/verkort',
    'Trap-distractor: vergelijkbaar klinkend woord of fout lettergreep-type',
    'Bij leenwoorden: bron-taal expliciet noemen in uitleg',
    'Tussen-n: meervoud-uitsluitings-regel altijd tonen',
    'true_false_series vermijden — past niet bij regel-toepassing',
  ],

  authoringGuideAnchor: '#6-spr--spelling-woordvorming',
}
